<template>
  <div>
    <!-- Feature row -->
    <div
      class="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
      :style="{ paddingLeft: `${8 + depth * 20}px` }"
      @click="handleRowClick"
    >
      <!-- Expand/collapse button -->
      <HLButton
        v-if="hasChildren"
        variant="text"
        size="2xs"
        @click.stop="toggleExpand"
        class="flex-shrink-0 w-4 h-4 p-0 min-w-0"
      >
        <Icon
          name="chevronRight"
          size="xs"
          class="transition-transform duration-200"
          :class="{ 'rotate-90': isExpanded }"
        />
      </HLButton>
      <div v-else class="w-4"></div>
      
      <!-- Checkbox -->
      <HLCheckbox
        :id="`feature-checkbox-${feature.id}`"
        :checked="isSelected"
        @click.stop="toggleSelect"
        class="flex-shrink-0"
      />
      
      <!-- Feature label -->
      <span class="flex-1 text-sm text-gray-700 dark:text-gray-300">
        {{ feature.featureId ? `${feature.featureId} ${feature.title || ''}`.trim() : (feature.title || feature.id) }}
      </span>
    </div>
    
    <!-- Children (recursive) -->
    <template v-if="isExpanded && hasChildren">
      <FeatureTreeNode
        v-for="child in feature.children"
        :key="child.id"
        :feature="child"
        :depth="depth + 1"
        :selected-features="selectedFeatures"
        :expanded-features="expandedFeatures"
        :search-query="searchQuery"
        @toggle-expand="$emit('toggle-expand', $event)"
        @toggle-select="$emit('toggle-select', $event)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { HLCheckbox, HLButton, HLIcon } from '@platform-ui/highrise'
import Icon from './Icon.vue'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  depth: {
    type: Number,
    default: 0
  },
  selectedFeatures: {
    type: Array,
    default: () => []
  },
  expandedFeatures: {
    type: Set,
    required: true
  },
  searchQuery: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['toggle-expand', 'toggle-select'])

const hasChildren = computed(() => {
  return props.feature.children && props.feature.children.length > 0
})

const isExpanded = computed(() => {
  return props.expandedFeatures.has(props.feature.id)
})

const isSelected = computed(() => {
  return props.selectedFeatures.includes(props.feature.id)
})

const toggleExpand = () => {
  emit('toggle-expand', props.feature.id)
}

const toggleSelect = () => {
  emit('toggle-select', props.feature.id)
}

const handleRowClick = () => {
  // Clicking the row toggles selection
  toggleSelect()
}
</script>

